'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetPositionsRow, useActiveWallet, useClaimPrize, useTurnkeySigningDeps } from '@morfi/backend-sdk-v2/client';
import { isI64 } from '@morfi/market-math';
import { useTranslations } from 'next-intl';
import type * as React from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import { buildShareUrl } from '@/components/portfolio/share/share-pnl-button.utils';
import { AnimatedBigInt } from '@/components/ui/animated-bigint';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Alert02Icon, Award01Icon, LinkForwardIconSolid } from '@/components/ui/icon/glyphs';
import { onEvent } from '@/events';
import { type OutcomeHueName, outcomeHueToStepVar, useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import type { UserPortfolioPositionDisplayRow } from '@/hooks/positions/user-portfolio/display-types';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { pnlColorClass } from '@/lib/market/position-pnl';
import { useUserNetwork } from '@/lib/network/client';
import { useMarketDetailPrefetchIntent } from '@/lib/sdk/prefetch';
import { ShareModal } from '@/lib/share';
import { cn } from '@/lib/ui/utils';
const ZERO: bigint = BigInt(0);
function bi(v: string | undefined): bigint {
  // Backend money fields are integer D6/D9 strings; guard with a validity test
  // rather than try/catch so a malformed wire value falls back to 0 without
  // exception-as-control-flow (FT-12).
  if (v === undefined || !/^-?\d+$/.test(v)) return ZERO;
  return BigInt(v);
}
type PortfolioPositionRow = GetPositionsRow | UserPortfolioPositionDisplayRow;
function unsignedI64Wire(value: string | undefined): bigint | null {
  if (value === undefined || !/^(?:0|[1-9]\d*)$/.test(value)) return null;
  const parsed = BigInt(value);
  return isI64(parsed) ? parsed : null;
}
type PositionRowProps = {
  row: UserPortfolioPositionDisplayRow;
  mode: 'open';
  isOwnPortfolio?: boolean;
  /** The row reflects server data fetched this session, so its claim state is current. */
  claimReady?: boolean;
} | {
  row: GetPositionsRow;
  mode: 'closed';
  isOwnPortfolio?: boolean;
  claimReady?: boolean;
};
const ROW_BASE_CLASS = 'group block rounded-panel-row border border-border-primary transition-colors duration-fast ease-default sm:hover:bg-background-tertiary';
export function PositionRow(props) {
  const $ = _c(83);
  const {
    row,
    mode,
    isOwnPortfolio: t0,
    claimReady: t1
  } = props;
  const isOwnPortfolio = t0 === undefined ? true : t0;
  const claimReady = t1 === undefined ? true : t1;
  const t = useTranslations("profile.stats.positions");
  const {
    formatD6,
    formatD6Signed
  } = useFormattedCurrency();
  const t2 = row.outcomeLabel ?? `Outcome ${row.outcomeIndex}`;
  let t3;
  if ($[0] !== row.outcomeIndex || $[1] !== t2) {
    t3 = {
      title: t2,
      index: row.outcomeIndex
    };
    $[0] = row.outcomeIndex;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    hue
  } = useOutcomeColor(t3);
  const label = row.outcomeLabel ?? `Outcome ${row.outcomeIndex}`;
  const isWon = row.resultState === "won_holding" || row.resultState === "won_sold";
  const isLost = row.resultState === "lost";
  const hasPrize = row.prizeState !== "none";
  const hasValuationBalance = mode === "open";
  const valueD6 = mode === "open" ? row.displayMark.currentValueD6 : null;
  const unrealized = mode === "open" ? row.displayMark.unrealisedPnlD6 : null;
  const realized = bi(row.realizedPnlD6);
  let t4;
  if ($[3] !== row.realizedPnlD6) {
    t4 = /^-?\d+$/.test(row.realizedPnlD6) ? BigInt(row.realizedPnlD6) : null;
    $[3] = row.realizedPnlD6;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const realizedAnimatedD6 = t4;
  const investedD6 = unsignedI64Wire(mode === "open" ? row.costBasisD6 : row.realizedCostBasisD6);
  let t5;
  if ($[5] !== row.realizedPenaltyD6) {
    t5 = unsignedI64Wire(row.realizedPenaltyD6);
    $[5] = row.realizedPenaltyD6;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const penaltyD6 = t5;
  const isWinner = row.positionType === "winner";
  const cardHref = isWinner ? `/winners/${row.outcomeMint}` : `/markets/${row.market}`;
  const intent = useIntentRoutePrefetch(cardHref, useMarketDetailPrefetchIntent(isWinner ? null : row.market).onMouseEnter);
  const canShare = row.wallet.length > 0;
  let t6;
  if ($[7] !== formatD6 || $[8] !== formatD6Signed || $[9] !== hasValuationBalance || $[10] !== label || $[11] !== row.market || $[12] !== row.marketTitle || $[13] !== row.realizedPnlD6 || $[14] !== t || $[15] !== unrealized || $[16] !== valueD6) {
    t6 = hasValuationBalance ? t("rowAriaLabelOpen", {
      market: row.marketTitle ?? row.market,
      outcome: label,
      value: formatD6((valueD6 ?? BigInt(0)).toString()),
      pnl: formatD6Signed((unrealized ?? BigInt(0)).toString())
    }) : t("rowAriaLabelClosed", {
      market: row.marketTitle ?? row.market,
      outcome: label,
      pnl: formatD6Signed(row.realizedPnlD6)
    });
    $[7] = formatD6;
    $[8] = formatD6Signed;
    $[9] = hasValuationBalance;
    $[10] = label;
    $[11] = row.market;
    $[12] = row.marketTitle;
    $[13] = row.realizedPnlD6;
    $[14] = t;
    $[15] = unrealized;
    $[16] = valueD6;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const ariaLabel = t6;
  let t7;
  if ($[18] !== row.market || $[19] !== row.outcomeIndex) {
    t7 = function handleClick() {
      onEvent("portfolio_position_sell_clicked", {
        marketPubkey: row.market,
        outcomeIndex: row.outcomeIndex
      });
    };
    $[18] = row.market;
    $[19] = row.outcomeIndex;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const handleClick = t7;
  const T0 = RowHead;
  let t8;
  if ($[21] !== formatD6 || $[22] !== formatD6Signed || $[23] !== hasValuationBalance || $[24] !== unrealized || $[25] !== valueD6) {
    t8 = hasValuationBalance ? {
      valueDisplay: formatD6((valueD6 ?? BigInt(0)).toString()),
      valueAnimatedD6: valueD6,
      unrealizedDisplay: formatD6Signed((unrealized ?? BigInt(0)).toString()),
      unrealizedAnimatedD6: unrealized,
      unrealizedTone: unrealized === null ? "text-foreground-disabled" : pnlColorClass(unrealized)
    } : undefined;
    $[21] = formatD6;
    $[22] = formatD6Signed;
    $[23] = hasValuationBalance;
    $[24] = unrealized;
    $[25] = valueD6;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const t9 = formatD6((investedD6 ?? BigInt(0)).toString());
  let t10;
  if ($[27] !== formatD6Signed || $[28] !== row.realizedPnlD6) {
    t10 = formatD6Signed(row.realizedPnlD6);
    $[27] = formatD6Signed;
    $[28] = row.realizedPnlD6;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const t11 = pnlColorClass(realized);
  let t12;
  if ($[30] !== T0 || $[31] !== canShare || $[32] !== hue || $[33] !== investedD6 || $[34] !== label || $[35] !== mode || $[36] !== penaltyD6 || $[37] !== realizedAnimatedD6 || $[38] !== row || $[39] !== t10 || $[40] !== t11 || $[41] !== t8 || $[42] !== t9) {
    t12 = <T0 row={row} hue={hue} label={label} mode={mode} reserveShareSpace={canShare} valuationMetrics={t8} investedDisplay={t9} investedAnimatedD6={investedD6} realizedDisplay={t10} realizedAnimatedD6={realizedAnimatedD6} realizedTone={t11} penaltyD6={penaltyD6} />;
    $[30] = T0;
    $[31] = canShare;
    $[32] = hue;
    $[33] = investedD6;
    $[34] = label;
    $[35] = mode;
    $[36] = penaltyD6;
    $[37] = realizedAnimatedD6;
    $[38] = row;
    $[39] = t10;
    $[40] = t11;
    $[41] = t8;
    $[42] = t9;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  const head = t12;
  const t13 = canShare && "sm:pr-16";
  let t14;
  if ($[44] !== t13) {
    t14 = cn("relative rounded-panel-row p-3.5", t13);
    $[44] = t13;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  const t15 = isOwnPortfolio ? handleClick : undefined;
  let t16;
  if ($[46] !== ariaLabel || $[47] !== cardHref || $[48] !== intent || $[49] !== t15) {
    t16 = <Link href={cardHref} prefetch={false} onClick={t15} aria-label={ariaLabel} {...intent} className="absolute inset-0 z-0 rounded-panel-row focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border-primary" />;
    $[46] = ariaLabel;
    $[47] = cardHref;
    $[48] = intent;
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== head || $[52] !== t14 || $[53] !== t16) {
    t17 = <div className={t14}>{t16}{head}</div>;
    $[51] = head;
    $[52] = t14;
    $[53] = t16;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  const linkedHead = t17;
  const showPrizeNote = isWon && hasPrize;
  const showLostNote = isLost;
  let t18;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = cn(ROW_BASE_CLASS, "overflow-hidden");
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== canShare || $[57] !== cardHref || $[58] !== hasValuationBalance || $[59] !== label || $[60] !== mode || $[61] !== row.market || $[62] !== row.marketTitle || $[63] !== row.outcomeIndex || $[64] !== row.realizedPnlD6 || $[65] !== row.wallet || $[66] !== unrealized) {
    t19 = canShare && <div className="absolute right-2 top-3.5 sm:top-1/2 sm:-translate-y-1/2"><ShareButton destinationPath={cardHref} marketPubkey={row.market} marketTitle={row.marketTitle ?? row.market} walletPubkey={row.wallet} outcomeIndex={row.outcomeIndex} outcomeTitle={label} closed={mode === "closed"} heroPnlD6={hasValuationBalance ? unrealized?.toString() ?? undefined : row.realizedPnlD6} /></div>;
    $[56] = canShare;
    $[57] = cardHref;
    $[58] = hasValuationBalance;
    $[59] = label;
    $[60] = mode;
    $[61] = row.market;
    $[62] = row.marketTitle;
    $[63] = row.outcomeIndex;
    $[64] = row.realizedPnlD6;
    $[65] = row.wallet;
    $[66] = unrealized;
    $[67] = t19;
  } else {
    t19 = $[67];
  }
  let t20;
  if ($[68] !== linkedHead || $[69] !== t19) {
    t20 = <div className="relative">{linkedHead}{t19}</div>;
    $[68] = linkedHead;
    $[69] = t19;
    $[70] = t20;
  } else {
    t20 = $[70];
  }
  let t21;
  if ($[71] !== claimReady || $[72] !== isOwnPortfolio || $[73] !== row || $[74] !== showPrizeNote) {
    t21 = showPrizeNote && <PrizeNote row={row} canClaim={isOwnPortfolio && claimReady} className="mx-3.5 mb-3.5" />;
    $[71] = claimReady;
    $[72] = isOwnPortfolio;
    $[73] = row;
    $[74] = showPrizeNote;
    $[75] = t21;
  } else {
    t21 = $[75];
  }
  let t22;
  if ($[76] !== showLostNote) {
    t22 = showLostNote && <LostNote className="mx-3.5 mb-3.5" />;
    $[76] = showLostNote;
    $[77] = t22;
  } else {
    t22 = $[77];
  }
  let t23;
  if ($[78] !== mode || $[79] !== t20 || $[80] !== t21 || $[81] !== t22) {
    t23 = <div data-testid="position-row" data-mode={mode} className={t18}>{t20}{t21}{t22}</div>;
    $[78] = mode;
    $[79] = t20;
    $[80] = t21;
    $[81] = t22;
    $[82] = t23;
  } else {
    t23 = $[82];
  }
  return t23;
}

// ─────────────────────────────────────────────────────────────────────────────
// RowHead — thumb · title (swatch + label + tag) · market title · metrics
// ─────────────────────────────────────────────────────────────────────────────

interface RowHeadProps {
  row: PortfolioPositionRow;
  hue: OutcomeHueName;
  label: string;
  mode: 'open' | 'closed';
  reserveShareSpace: boolean;
  valuationMetrics: {
    valueDisplay: string;
    valueAnimatedD6: bigint | null;
    unrealizedDisplay: string;
    unrealizedAnimatedD6: bigint | null;
    unrealizedTone: string;
  } | undefined;
  investedDisplay: string;
  investedAnimatedD6: bigint | null;
  realizedDisplay: string | null;
  realizedAnimatedD6: bigint | null;
  realizedTone: string;
  /** Conviction penalty already booked into `realizedDisplay`; shown only when charged. */
  penaltyD6: bigint | null;
}
function RowHead(t0) {
  const $ = _c(56);
  const {
    row,
    hue,
    label,
    mode,
    reserveShareSpace,
    valuationMetrics,
    investedDisplay,
    investedAnimatedD6,
    realizedDisplay,
    realizedAnimatedD6,
    realizedTone,
    penaltyD6
  } = t0;
  const t = useTranslations("profile.stats.positions");
  const feeT = useTranslations("trade.fees");
  const {
    formatD6,
    formatD6Signed
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== hue) {
    t1 = outcomeHueToStepVar(hue, 400);
    $[0] = hue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const swatch = t1;
  const isWinner = row.positionType === "winner";
  const isWon = row.resultState === "won_holding" || row.resultState === "won_sold";
  const isLost = row.resultState === "lost";
  const showDot = isWinner || isWon;
  const t2 = reserveShareSpace && "pr-10 sm:pr-0";
  let t3;
  if ($[2] !== t2) {
    t3 = cn("flex min-w-0 items-center gap-3.5 sm:flex-1", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== hue || $[5] !== isLost || $[6] !== row || $[7] !== showDot) {
    t4 = <PositionThumb row={row} hue={hue} showDot={showDot} dim={isLost} />;
    $[4] = hue;
    $[5] = isLost;
    $[6] = row;
    $[7] = showDot;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== row.marketType || $[10] !== t4) {
    t5 = <ThumbWithOrigin marketType={row.marketType}>{t4}</ThumbWithOrigin>;
    $[9] = row.marketType;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== isWinner || $[13] !== swatch) {
    t6 = !isWinner && <span aria-hidden={true} className="size-2 shrink-0 rounded-full" style={{
      backgroundColor: swatch
    }} />;
    $[12] = isWinner;
    $[13] = swatch;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== label) {
    t7 = <span className="truncate text-body font-semibold text-foreground-primary">{label}</span>;
    $[15] = label;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== hue || $[18] !== isWinner || $[19] !== t) {
    t8 = <PositionTypeTag isWinner={isWinner} hue={hue} t={t} />;
    $[17] = hue;
    $[18] = isWinner;
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t6 || $[22] !== t7 || $[23] !== t8) {
    t9 = <div className="flex min-w-0 items-center gap-1.5">{t6}{t7}{t8}</div>;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== isWinner || $[26] !== row.market || $[27] !== row.marketTitle) {
    t10 = !isWinner && <span className="truncate text-caption text-foreground-secondary">{row.marketTitle ?? row.market}</span>;
    $[25] = isWinner;
    $[26] = row.market;
    $[27] = row.marketTitle;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== mode || $[30] !== row.resultState || $[31] !== t) {
    t11 = mode !== "open" && row.resultState !== "sold_out" && <div className="mt-1.5"><ResultPill resultState={row.resultState} t={t} /></div>;
    $[29] = mode;
    $[30] = row.resultState;
    $[31] = t;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t10 || $[34] !== t11 || $[35] !== t9) {
    t12 = <div className="flex min-w-0 flex-1 flex-col">{t9}{t10}{t11}</div>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t9;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t12 || $[38] !== t3 || $[39] !== t5) {
    t13 = <div data-testid="position-row-identity" className={t3}>{t5}{t12}</div>;
    $[37] = t12;
    $[38] = t3;
    $[39] = t5;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== feeT || $[42] !== formatD6 || $[43] !== formatD6Signed || $[44] !== investedAnimatedD6 || $[45] !== investedDisplay || $[46] !== penaltyD6 || $[47] !== realizedAnimatedD6 || $[48] !== realizedDisplay || $[49] !== realizedTone || $[50] !== t || $[51] !== valuationMetrics) {
    t14 = valuationMetrics !== undefined ? <div className="grid grid-cols-2 gap-2 sm:flex sm:shrink-0 sm:items-stretch sm:gap-6"><Metric label={t("invested")} value={investedDisplay} animatedValue={investedAnimatedD6} format={value => formatD6(value.toString())} /><Metric label={t("value")} value={valuationMetrics.valueDisplay} animatedValue={valuationMetrics.valueAnimatedD6} format={value_0 => formatD6(value_0.toString())} testId="position-value" /><Metric label={t("unrealised")} value={valuationMetrics.unrealizedDisplay} animatedValue={valuationMetrics.unrealizedAnimatedD6} format={value_1 => formatD6Signed(value_1.toString())} tone={valuationMetrics.unrealizedTone} testId="position-unrealized" /><Metric label={t("realised")} value={realizedDisplay ?? formatD6Signed("0")} animatedValue={realizedAnimatedD6} format={value_2 => formatD6Signed(value_2.toString())} tone={realizedDisplay === null ? "text-foreground-disabled" : realizedTone} />{penaltyD6 !== null && penaltyD6 > BigInt(0) && <Metric label={feeT("penalty_fee")} value={formatD6(penaltyD6.toString())} animatedValue={penaltyD6} format={value_3 => formatD6(value_3.toString())} testId="position-penalty" />}</div> : <div className="grid grid-cols-2 gap-2 sm:flex sm:shrink-0 sm:items-stretch sm:gap-6"><Metric label={t("invested")} value={investedDisplay} animatedValue={investedAnimatedD6} format={value_4 => formatD6(value_4.toString())} /><Metric label={t("realisedPnl")} value={realizedDisplay ?? formatD6Signed("0")} animatedValue={realizedAnimatedD6} format={value_5 => formatD6Signed(value_5.toString())} tone={realizedDisplay === null ? "text-foreground-disabled" : realizedTone} />{penaltyD6 !== null && penaltyD6 > BigInt(0) && <Metric label={feeT("penalty_fee")} value={formatD6(penaltyD6.toString())} animatedValue={penaltyD6} format={value_6 => formatD6(value_6.toString())} testId="position-penalty" />}</div>;
    $[41] = feeT;
    $[42] = formatD6;
    $[43] = formatD6Signed;
    $[44] = investedAnimatedD6;
    $[45] = investedDisplay;
    $[46] = penaltyD6;
    $[47] = realizedAnimatedD6;
    $[48] = realizedDisplay;
    $[49] = realizedTone;
    $[50] = t;
    $[51] = valuationMetrics;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== t13 || $[54] !== t14) {
    t15 = <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3.5">{t13}{t14}</div>;
    $[53] = t13;
    $[54] = t14;
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  return t15;
}

// ─────────────────────────────────────────────────────────────────────────────
// ResultPill — closed-mode status pill (won · sold / sold out / lost)
// ─────────────────────────────────────────────────────────────────────────────

function ResultPill(t0) {
  const $ = _c(33);
  const {
    resultState,
    t
  } = t0;
  let t1;
  if ($[0] !== t) {
    t1 = t("pillWonLive");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      tone: "won",
      label: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("pillWonSold");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = {
      tone: "won",
      label: t3
    };
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("pillLost");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = {
      tone: "lost",
      label: t5
    };
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t) {
    t7 = t("pillSold");
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = {
      tone: "sold",
      label: t7
    };
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t) {
    t9 = t("pillOpen");
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = {
      tone: "sold",
      label: t9
    };
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t2 || $[22] !== t4 || $[23] !== t6 || $[24] !== t8) {
    t11 = {
      won_holding: t2,
      won_sold: t4,
      lost: t6,
      sold_out: t8,
      open: t10
    };
    $[20] = t10;
    $[21] = t2;
    $[22] = t4;
    $[23] = t6;
    $[24] = t8;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const map = t11 as const;
  const {
    tone,
    label
  } = map[resultState];
  const t12 = tone === "won" && "bg-feedback-success-surface text-feedback-success-content";
  const t13 = tone === "lost" && "bg-feedback-error-surface text-feedback-error-content";
  const t14 = tone === "sold" && "bg-background-tertiary text-foreground-secondary";
  let t15;
  if ($[26] !== t12 || $[27] !== t13 || $[28] !== t14) {
    t15 = cn("inline-flex items-center rounded-pill px-2 py-0.5 text-micro font-semibold", t12, t13, t14);
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== label || $[31] !== t15) {
    t16 = <span className={t15}>{label}</span>;
    $[30] = label;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  return t16;
}
interface MetricProps {
  label: string;
  value: string;
  animatedValue: bigint | null;
  format: (value: bigint) => string;
  tone?: string;
  testId?: string;
}

// Mobile: a translucent stat tile matching the PnL card (`position-card.tsx`).
// Desktop (sm+): the original plain right-aligned inline metric — no tile.
function Metric(t0) {
  const $ = _c(15);
  const {
    label,
    value,
    animatedValue,
    format,
    tone,
    testId
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="truncate text-caption text-foreground-tertiary">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== tone) {
    t2 = cn("truncate text-body-lg font-medium tabular-nums text-foreground-primary", "sm:text-body sm:font-semibold", tone);
    $[2] = tone;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== animatedValue || $[5] !== format || $[6] !== value) {
    t3 = animatedValue === null ? value : <AnimatedBigInt value={animatedValue} format={format} />;
    $[4] = animatedValue;
    $[5] = format;
    $[6] = value;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== testId) {
    t4 = <span data-testid={testId} className={t2}>{t3}</span>;
    $[8] = t2;
    $[9] = t3;
    $[10] = testId;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t1 || $[13] !== t4) {
    t5 = <div className="flex min-w-0 flex-col gap-1 rounded-panel-row bg-background-tertiary/50 px-3 py-3 sm:items-end sm:justify-center sm:gap-0 sm:bg-transparent sm:p-0 sm:text-right">{t1}{t4}</div>;
    $[12] = t1;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}

// ─────────────────────────────────────────────────────────────────────────────
// PositionTypeTag — outcome-hued "Market" / green "Winner"
// ─────────────────────────────────────────────────────────────────────────────

function PositionTypeTag(t0) {
  const $ = _c(8);
  const {
    isWinner,
    hue,
    t
  } = t0;
  const t1 = isWinner ? "bg-feedback-success-accent/10 text-feedback-success-content" : `bg-outcome-${hue}-outline text-outcome-${hue}-text`;
  let t2;
  if ($[0] !== t1) {
    t2 = cn("inline-flex shrink-0 items-center rounded-pill px-2 py-0.5 text-micro font-semibold", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== isWinner || $[3] !== t) {
    t3 = isWinner ? t("tagWinner") : t("tagMarket");
    $[2] = isWinner;
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <span className={t2}>{t3}</span>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}

// ─────────────────────────────────────────────────────────────────────────────
// PositionThumb — 40×40 outcome image with optional win/hue status dot
// ─────────────────────────────────────────────────────────────────────────────

function PositionThumb(t0) {
  const $ = _c(17);
  const {
    row,
    hue,
    showDot,
    dim
  } = t0;
  const t1 = dim && "opacity-55";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("relative size-10 shrink-0", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== hue || $[3] !== row.market || $[4] !== row.outcomeImageNsfw || $[5] !== row.outcomeImageUrl || $[6] !== row.outcomeIndex) {
    t3 = {
      kind: "outcome",
      marketPubkey: row.market,
      outcomeIndex: row.outcomeIndex,
      hue,
      imageUrl: row.outcomeImageUrl,
      nsfw: row.outcomeImageNsfw
    };
    $[2] = hue;
    $[3] = row.market;
    $[4] = row.outcomeImageNsfw;
    $[5] = row.outcomeImageUrl;
    $[6] = row.outcomeIndex;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = row.outcomeLabel ?? "";
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <OptimizedImage variant="thumbnail" entity={t3} alt={t4} size="size-10" rounded="rounded-control" preview={false} />;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== showDot) {
    t6 = showDot && <span aria-hidden={true} className="absolute -right-1 -bottom-1 size-[13px] rounded-full border-2 border-background-primary bg-feedback-success-accent" />;
    $[11] = showDot;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t2 || $[14] !== t5 || $[15] !== t6) {
    t7 = <div className={t2}>{t5}{t6}</div>;
    $[13] = t2;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}

// ─────────────────────────────────────────────────────────────────────────────
// PrizeNote — inline "Won the prize pool" with a Claim CTA / claimed amount
// ─────────────────────────────────────────────────────────────────────────────

function PrizeNote(t0) {
  const $ = _c(31);
  const {
    row,
    canClaim,
    className
  } = t0;
  const t = useTranslations("profile.stats.positions");
  const tClaim = useTranslations("market.claim");
  const {
    formatD6,
    formatD6Signed
  } = useFormattedCurrency();
  const claim = useClaimPrize();
  const {
    activeWallet
  } = useActiveWallet();
  const signingDeps = useTurnkeySigningDeps();
  const claimConfirmedHere = claim.data?.ok === true && claim.variables?.marketAddress === row.market && claim.variables?.activeWallet.address === activeWallet?.address;
  const claimed = row.prizeState === "claimed" || claimConfirmedHere;
  const claimedAmountD6 = row.prizeState === "claimed" ? row.prizeClaimedD6 : row.prizeClaimableD6;
  let t1;
  if ($[0] !== activeWallet || $[1] !== claim || $[2] !== row.claimRightsBalanceD6 || $[3] !== row.market || $[4] !== signingDeps) {
    t1 = function onClaim() {
      if (!activeWallet || signingDeps === undefined) {
        return;
      }
      claim.mutate({
        marketAddress: row.market,
        burnAmountD6: row.claimRightsBalanceD6,
        activeWallet: {
          id: activeWallet.id,
          address: activeWallet.address,
          type: activeWallet.type
        },
        signingDeps
      });
    };
    $[0] = activeWallet;
    $[1] = claim;
    $[2] = row.claimRightsBalanceD6;
    $[3] = row.market;
    $[4] = signingDeps;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const onClaim = t1;
  let t2;
  if ($[6] !== className) {
    t2 = cn("flex min-h-10 items-center justify-between gap-2.5 rounded-control border border-feedback-success-border bg-feedback-success-surface py-2 pl-3.5 pr-2 text-caption font-semibold text-feedback-success-content", className);
    $[6] = className;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Icon icon={Award01Icon} className="size-3.5 shrink-0" />;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== claimed || $[10] !== claimedAmountD6 || $[11] !== formatD6Signed || $[12] !== t) {
    t4 = claimed ? t("prizeClaimed", {
      amount: formatD6Signed(claimedAmountD6)
    }) : t("prizeWon");
    $[9] = claimed;
    $[10] = claimedAmountD6;
    $[11] = formatD6Signed;
    $[12] = t;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t4) {
    t5 = <span className="flex items-center gap-1.5">{t3}{t4}</span>;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== activeWallet?.address || $[17] !== canClaim || $[18] !== claim.isPending || $[19] !== claimed || $[20] !== formatD6 || $[21] !== onClaim || $[22] !== row.prizeClaimableD6 || $[23] !== row.wallet || $[24] !== t || $[25] !== tClaim) {
    t6 = !claimed && canClaim && row.wallet === activeWallet?.address && <AnimatedButton variant="primary" size="sm" onClick={onClaim} disabled={claim.isPending} loading={claim.isPending} className="px-3.5 text-caption">{claim.isPending ? tClaim("claiming") : t("claimAmount", {
        amount: formatD6(row.prizeClaimableD6)
      })}</AnimatedButton>;
    $[16] = activeWallet?.address;
    $[17] = canClaim;
    $[18] = claim.isPending;
    $[19] = claimed;
    $[20] = formatD6;
    $[21] = onClaim;
    $[22] = row.prizeClaimableD6;
    $[23] = row.wallet;
    $[24] = t;
    $[25] = tClaim;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== t2 || $[28] !== t5 || $[29] !== t6) {
    t7 = <div className={t2}>{t5}{t6}</div>;
    $[27] = t2;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  return t7;
}

// ─────────────────────────────────────────────────────────────────────────────
// LostNote — red drained-to-$0 warning
// ─────────────────────────────────────────────────────────────────────────────

function LostNote(t0) {
  const $ = _c(10);
  const {
    className
  } = t0;
  const t = useTranslations("profile.stats.positions");
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex min-h-10 items-center justify-center gap-1.5 rounded-control border border-feedback-error-border bg-feedback-error-surface px-3 py-2 text-caption font-medium text-feedback-error-content", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Icon icon={Alert02Icon} className="size-3.5 shrink-0" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t("drainedNote");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <span className="text-center">{t3}</span>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t4) {
    t5 = <div className={t1}>{t2}{t4}</div>;
    $[7] = t1;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function ShareButton(t0) {
  const $ = _c(38);
  const {
    destinationPath,
    marketPubkey,
    marketTitle,
    walletPubkey,
    outcomeIndex,
    outcomeTitle,
    closed,
    heroPnlD6
  } = t0;
  const t = useTranslations("profile.stats.positions");
  const tShare = useTranslations("portfolio.share");
  const {
    formatD6Signed
  } = useFormattedCurrency();
  const {
    network
  } = useUserNetwork();
  const t1 = network === "mainnet" ? "mainnet" : "devnet";
  let t2;
  if ($[0] !== closed || $[1] !== destinationPath || $[2] !== heroPnlD6 || $[3] !== marketPubkey || $[4] !== outcomeIndex || $[5] !== t1 || $[6] !== walletPubkey) {
    t2 = buildShareUrl(marketPubkey, walletPubkey, outcomeIndex, t1, heroPnlD6, closed, destinationPath);
    $[0] = closed;
    $[1] = destinationPath;
    $[2] = heroPnlD6;
    $[3] = marketPubkey;
    $[4] = outcomeIndex;
    $[5] = t1;
    $[6] = walletPubkey;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const shareUrl = t2;
  let t3;
  if ($[8] !== formatD6Signed || $[9] !== heroPnlD6 || $[10] !== marketTitle || $[11] !== outcomeTitle || $[12] !== tShare) {
    const pnlText = heroPnlD6 !== undefined && heroPnlD6.length > 0 && heroPnlD6 !== "0" ? formatD6Signed(heroPnlD6) : null;
    t3 = pnlText === null ? tShare("tweetText", {
      marketTitle,
      outcomeLabel: outcomeTitle
    }) : tShare("tweetTextWithPnl", {
      pnl: pnlText,
      marketTitle,
      outcomeLabel: outcomeTitle
    });
    $[8] = formatD6Signed;
    $[9] = heroPnlD6;
    $[10] = marketTitle;
    $[11] = outcomeTitle;
    $[12] = tShare;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const tweetText = t3;
  let t4;
  if ($[14] !== heroPnlD6) {
    t4 = heroPnlD6 !== undefined ? {
      cacheVersion: heroPnlD6
    } : {};
    $[14] = heroPnlD6;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== closed) {
    t5 = closed ? {
      closed: true
    } : {};
    $[16] = closed;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== marketPubkey || $[19] !== outcomeIndex || $[20] !== t4 || $[21] !== t5 || $[22] !== walletPubkey) {
    t6 = {
      kind: "pnl_position",
      marketPubkey,
      walletPubkey,
      outcomeIndex,
      ...t4,
      ...t5
    };
    $[18] = marketPubkey;
    $[19] = outcomeIndex;
    $[20] = t4;
    $[21] = t5;
    $[22] = walletPubkey;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== tShare) {
    t7 = tShare("modalTitle");
    $[24] = tShare;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== t) {
    t8 = t("shareAriaLabel");
    $[26] = t;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t10;
  let t9;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn("grid size-8 place-items-center rounded-control bg-background-tertiary text-foreground-tertiary", "transition-colors duration-fast ease-default hover:text-foreground-brand", "motion-reduce:transition-none", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary");
    t10 = <Icon icon={LinkForwardIconSolid} className="size-4" />;
    $[28] = t10;
    $[29] = t9;
  } else {
    t10 = $[28];
    t9 = $[29];
  }
  let t11;
  if ($[30] !== t8) {
    t11 = <button type="button" data-testid="position-row-share-link" aria-label={t8} onClick={_temp} className={t9}>{t10}</button>;
    $[30] = t8;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== shareUrl || $[33] !== t11 || $[34] !== t6 || $[35] !== t7 || $[36] !== tweetText) {
    t12 = <ShareModal surface={t6} url={shareUrl} text={tweetText} title={t7} trigger={t11} />;
    $[32] = shareUrl;
    $[33] = t11;
    $[34] = t6;
    $[35] = t7;
    $[36] = tweetText;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  return t12;
}
function _temp(event) {
  event.stopPropagation();
}